import * as turf from "@turf/turf";
import * as SpatialIndex from "./SpatialIndex.js";
import { createDimensionPopup } from "./mapUtils.js";
import {
  stationDataActions,
  viewMode,
  mapInstance,
  drawInstance,
  filterActions
} from "../../services/station-service.ts";

const COORD_PRECISION = 4; // ~11 m

// A drawn feature's point (circle centre / polygon centroid) as {lat, lng} strings, or null.
function featureToLatLng(feature) {
  try {
    let lng, lat;
    if (feature.properties?.isCircle && feature.properties.center) {
      [lng, lat] = feature.properties.center;
    } else if (feature.geometry?.type === "Polygon") {
      [lng, lat] = turf.centroid(feature).geometry.coordinates;
    } else {
      return null;
    }
    if (!Number.isFinite(lng) || !Number.isFinite(lat)) return null;
    return { lat: lat.toFixed(COORD_PRECISION), lng: lng.toFixed(COORD_PRECISION) };
  } catch (error) {
    console.warn("Failed to derive coordinates from drawn feature:", error);
    return null;
  }
}

export function useDrawingControls() {
  let map = null;
  let draw = null;
  let dimensionPopup = null;
  let polygonButtonRef = null;
  let circleButtonRef = null;
  let onRefreshCallback = null;
  let onShapeDeletedCallback = null;
  let isProcessingShape = false;

  function hasActiveShape() {
    if (!draw) return false;
    const mode = draw.getMode();
    if (mode === 'draw_polygon' || mode === 'drag_circle') return false;
    return draw.getAll().features.length > 0;
  }

  function showDimensions(feature) {
    if (dimensionPopup) {
      dimensionPopup.remove();
    }
    dimensionPopup = createDimensionPopup(feature, map);
  }

  function updateButtonState() {
    const isDisabled = hasActiveShape();
    [polygonButtonRef, circleButtonRef].forEach((button) => {
      if (button) {
        button.disabled = isDisabled;
        button.title = isDisabled
          ? "Only one shape allowed (delete current to draw another)"
          : "Draw a shape";
      }
    });
  }

  function calculateCircleRadius(center, geometry) {
    if (geometry.type === "Polygon" && geometry.coordinates[0].length > 0) {
      const firstPoint = geometry.coordinates[0][0];
      return turf.distance(
        turf.point(center),
        turf.point(firstPoint),
        { units: "kilometers" }
      );
    }
    return null;
  }

  async function processShape(drawnFeature) {
    const geometry = drawnFeature.geometry;
    const fetchParams = { limit: 1000 };

    if (drawnFeature.properties.isCircle) {
      const center = drawnFeature.properties.center;
      let radius = calculateCircleRadius(center, geometry);
      if (!radius) {
        radius = drawnFeature.properties.radiusKm || drawnFeature.properties.radius;
      }

      if (!radius || radius <= 0) {
        throw new Error("Invalid circle radius");
      }
      drawnFeature.properties.radiusKm = radius;
      draw.setFeatureProperty(drawnFeature.id, "radiusKm", radius);
      draw.setFeatureProperty(drawnFeature.id, "center", center);
      draw.setFeatureProperty(drawnFeature.id, "isCircle", true);

      fetchParams.radius = `${center[0]},${center[1]},${radius}km`;
    } else if (geometry.type === "Polygon") {
      const bbox = turf.bbox(geometry);
      fetchParams.bbox = `${bbox[3]},${bbox[0]},${bbox[1]},${bbox[2]}`;
    }

    return fetchParams;
  }

  async function handleDrawCreate(e) {
    if (isProcessingShape) return;

    if (draw.getAll().features.length > 1) {
      const newFeatureId = e.features[0].id;
      setTimeout(() => draw.delete([newFeatureId]), 0);
      return;
    }

    isProcessingShape = true;

    try {
      const drawnFeature = e.features[0];

      // Clear any coordinate first so it can't hijack the shape's fetch.
      filterActions.clearLocationFilter();

      const fetchParams = await processShape(drawnFeature);
      await stationDataActions.loadStations(fetchParams);

      const stationsInShape = SpatialIndex.findStationsInFeature(drawnFeature);
      filterActions.setSpatialFilter({
        filteredStations: stationsInShape.map((s) => s.id)
      });

      // Show the shape's coordinates in the boxes.
      const coords = featureToLatLng(drawnFeature);
      if (coords) filterActions.setCoordinateDisplay(coords.lat, coords.lng);

      showDimensions(drawnFeature);
      updateButtonState();

      setTimeout(() => {
        draw.changeMode("simple_select", { featureIds: [drawnFeature.id] });
      }, 100);
    } catch (error) {
      console.error("Error processing drawn shape:", error);
      filterActions.clearSpatialFilter();
    } finally {
      isProcessingShape = false;
    }
  }

  async function handleDrawUpdate(e) {
    if (isProcessingShape) return;

    const drawnFeature = e.features[0];
    
    if (drawnFeature.properties?.isCircle) {
      await handleDrawCreate(e);
      return;
    }
    
    if (SpatialIndex.isInitialized()) {
      const cachedResults = SpatialIndex.findStationsInFeature(drawnFeature);

      if (cachedResults.length > 0) {
        filterActions.setSpatialFilter({
          filteredStations: cachedResults.map((s) => s.id)
        });

        // Keep the boxes in step with the moved polygon.
        const coords = featureToLatLng(drawnFeature);
        if (coords) filterActions.setCoordinateDisplay(coords.lat, coords.lng);

        if (dimensionPopup) {
          dimensionPopup.remove();
        }
        dimensionPopup = createDimensionPopup(drawnFeature, map);
        return;
      }
    }

    await handleDrawCreate(e);
  }

  function handleDrawDelete() {
    if (dimensionPopup) {
      dimensionPopup.remove();
      dimensionPopup = null;
    }

    if (!hasActiveShape()) {
      filterActions.clearSpatialFilter();
      filterActions.clearCoordinateDisplay();
      viewMode.set("viewport");

      if (onShapeDeletedCallback) {
        onShapeDeletedCallback();
      }

      setTimeout(() => {
        if (onRefreshCallback) {
          onRefreshCallback();
        }
      }, 100);
    }
    updateButtonState();
  }

  function setup(mapInst, drawInst, { onRefresh, onShapeDeleted }) {
    map = mapInst;
    draw = drawInst;
    onRefreshCallback = onRefresh;
    onShapeDeletedCallback = onShapeDeleted;

    mapInstance.set(mapInst);
    drawInstance.set(drawInst);

    map.on("draw.create", handleDrawCreate);
    map.on("draw.update", handleDrawUpdate);
    map.on("draw.delete", handleDrawDelete);
  }

  function cleanup() {
    isProcessingShape = false;

    if (map) {
      map.off("draw.create", handleDrawCreate);
      map.off("draw.update", handleDrawUpdate);
      map.off("draw.delete", handleDrawDelete);
    }
    if (dimensionPopup) {
      dimensionPopup.remove();
    }
  }

  function deleteAllShapes() {
    if (!draw) return;
    map.off("draw.delete", handleDrawDelete);
    draw.deleteAll();
    map.on("draw.delete", handleDrawDelete);
    handleDrawDelete();
  }

  // Remove the shape when a coordinate search takes over, without the viewport reset.
  function clearShapeForCoordinateSearch() {
    if (!draw || !hasActiveShape()) return;

    if (dimensionPopup) {
      dimensionPopup.remove();
      dimensionPopup = null;
    }

    map.off("draw.delete", handleDrawDelete);
    draw.deleteAll();
    map.on("draw.delete", handleDrawDelete);

    filterActions.clearSpatialFilter();

    if (onShapeDeletedCallback) {
      onShapeDeletedCallback();
    }
    updateButtonState();
  }

  function setButtonRefs(polygonBtn, circleBtn) {
    polygonButtonRef = polygonBtn;
    circleButtonRef = circleBtn;
    updateButtonState();
  }

  return {
    setup,
    cleanup,
    setButtonRefs,
    hasActiveShape,
    deleteAllShapes,
    clearShapeForCoordinateSearch
  };
}